import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Eye, Star, RefreshCw, ArrowLeft, Award } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

const BOARDS = [
  { id: 'popular', label: 'Most Popular', icon: Eye },
  { id: 'rated', label: 'Highest Rated', icon: Star },
  { id: 'reruns', label: 'Most Re-runs', icon: RefreshCw },
];

const PERIODS = [
  { id: 'all', label: 'All Time' },
  { id: 'month', label: 'This Month' },
  { id: 'week', label: 'This Week' },
];

export default function CatFightLeaderboard() {
  const [board, setBoard] = useState('popular');
  const [period, setPeriod] = useState('all');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, [board, period]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/api/leaderboard/cat-fights?board=${board}&period=${period}&limit=25`);
      setEntries(res.data.entries || []);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-4xl mx-auto">
        <Link to="/gallery/cat-fights" className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#EF4444] font-mono mb-4">
          <ArrowLeft className="w-3 h-3" /> Gallery
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Trophy className="w-6 h-6 text-[#F59E0B]" />
          <h1 className="text-2xl font-bold font-['Barlow_Condensed']">Cat Fight Leaderboard</h1>
        </div>

        {/* Board selector */}
        <div className="flex gap-2 mb-4">
          {BOARDS.map(b => (
            <button key={b.id} onClick={() => setBoard(b.id)}
              className={`px-4 py-2 text-xs font-mono flex items-center gap-1.5 border transition-colors ${
                board === b.id ? 'bg-[#EF4444] text-white border-[#EF4444]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#EF4444]/30'
              }`}>
              <b.icon className="w-3 h-3" /> {b.label}
            </button>
          ))}
        </div>

        {/* Period filter */}
        <div className="flex gap-1 mb-6">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1 text-[10px] font-mono border ${
                period === p.id ? 'text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/5' : 'border-[#2A2A2A] text-[#A0A0A0]'
              }`}>{p.label}</button>
          ))}
        </div>

        {/* Leaderboard table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-[#A0A0A0] text-sm text-center py-12">No entries yet{board === 'rated' ? ' (minimum 3 ratings required)' : ''}.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, i) => (
              <motion.div key={entry.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
                <Link to={`/gallery/cat-fights/${entry.id}`}
                  className="block p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#EF4444]/30 transition-colors">
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <span className={`text-2xl font-bold font-['Barlow_Condensed'] w-10 text-center ${
                      entry.rank === 1 ? 'text-[#F59E0B]' : entry.rank === 2 ? 'text-[#A0A0A0]' : entry.rank === 3 ? 'text-[#CD7F32]' : 'text-[#2A2A2A]'
                    }`}>#{entry.rank}</span>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold font-['Barlow_Condensed'] truncate">{entry.topic}</span>
                        {entry.hall_of_fame && (
                          <span className="text-[8px] px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 font-mono flex items-center gap-0.5">
                            <Award className="w-2.5 h-2.5" /> HOF
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[9px] font-mono text-[#A0A0A0]">
                        <span>by {entry.creator}</span>
                        {entry.winner && <span>Winner: <span className="text-[#EF4444]">{entry.winner}</span></span>}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-[10px] font-mono text-[#A0A0A0] shrink-0">
                      <div className="text-center">
                        <Eye className="w-3 h-3 mx-auto mb-0.5" />
                        {entry.view_count}
                      </div>
                      <div className="text-center">
                        <Star className="w-3 h-3 mx-auto mb-0.5 text-[#F59E0B]" />
                        {entry.avg_rating || '—'}
                      </div>
                      <div className="text-center">
                        <RefreshCw className="w-3 h-3 mx-auto mb-0.5" />
                        {entry.rerun_count}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
